import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Beam merchant dashboard where the void is actually performed (hard-coded per request).
const BEAM_DASHBOARD_URL = 'https://lighthouse.beamcheckout.com/merchant/smilensg/single-links';
const fmtDate = (d) => d ? new Date(d).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

// Public, mobile-first page Gain/North open from the LINE void-check message. They copy the
// Beam purchaseId, open Beam to void, then here: upload the void-confirmation image and
// confirm ('void_done' → money back on the card, case moves to admin-notify). If it can't be
// voided, 'reject' drops it into the normal bank-refund flow (+3.5%).
export default function VoidCheckPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // 'void_notify' | 'pending' after resolve
  const [submitError, setSubmitError] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/order/void-check/${token}`)
      .then((r) => setInfo(r.data))
      .catch((err) => setFetchError(err.response?.data?.message || 'ไม่พบรายการหรือ Link หมดอายุ'));
  }, [token]);

  const copy = (text) => {
    navigator.clipboard.writeText(String(text || '')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  const confirmVoid = async () => {
    const file = fileRef.current?.files[0];
    if (!file) { setSubmitError('กรุณาแนบรูปยืนยันการ void'); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const fd = new FormData();
      fd.append('action', 'void_done');
      fd.append('image', file);
      const res = await axios.post(`${BACKEND_URL}/api/order/void-check/${token}`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data.cancelStage); // 'void_notify'
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'บันทึกไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setSubmitting(false);
    }
  };

  const rejectVoid = async () => {
    if (!window.confirm('ยืนยันว่า void ไม่ได้? จะส่งเข้ารายการคืนเงินปกติ (+3.5%)')) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/order/void-check/${token}`, { action: 'reject' });
      setResult(res.data.cancelStage); // 'pending'
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'ดำเนินการไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setSubmitting(false);
    }
  };

  if (fetchError) {
    return (
      <Shell>
        <div className="text-5xl mb-4">⚠️</div>
        <p className="text-red-600 font-semibold text-lg mb-2">ไม่สามารถเปิดหน้านี้ได้</p>
        <p className="text-gray-500 text-sm">{fetchError}</p>
      </Shell>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">กำลังโหลด...</p>
      </div>
    );
  }

  // Resolved before this load, or just resolved now. void_notify/voided = the void went
  // through (money back on the card); pending = sent to the normal refund flow.
  const finalStage = result || (info.resolved ? info.cancelStage : null);
  if (finalStage) {
    const voided = finalStage === 'void_notify' || finalStage === 'voided';
    return (
      <Shell>
        <div className="text-5xl mb-4">{voided ? '✅' : '🏦'}</div>
        <p className={`font-bold text-xl mb-2 ${voided ? 'text-green-700' : 'text-blue-700'}`}>
          {voided ? 'บันทึก void แล้ว' : 'ส่งเข้ารายการคืนเงินแล้ว'}
        </p>
        <p className="text-gray-600 text-sm">
          {voided
            ? 'เงินถูกคืนเข้าบัตรเรียบร้อย — แอดมินจะแจ้งลูกค้าต่อไป'
            : 'รายการถูกส่งไปยังขั้นตอนการโอนเงินคืนตามปกติ (บวกค่าธรรมเนียมบัตร 3.5%)'}
        </p>
      </Shell>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center p-3">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full overflow-hidden self-start">

        {/* Header */}
        <div className="bg-orange-800 px-5 py-4">
          <p className="text-orange-200 text-xs mb-0.5">💳 เช็ค VOID บัตรเครดิต</p>
          <p className="text-white text-lg font-bold leading-tight">{info.contact || info.smileName || '-'}</p>
          {info.smileName && info.contact && <p className="text-orange-200 text-sm">{info.smileName}</p>}
          {info.paymentCompletedAt && (
            <p className="text-orange-200 text-xs mt-1">จ่ายเมื่อ {fmtDate(info.paymentCompletedAt)}</p>
          )}
        </div>

        <div className="px-5 py-4 space-y-4">
          {info.url && (
            <div>
              <p className="text-xs text-gray-500 mb-1">ลิงก์สินค้า</p>
              <a href={info.url} target="_blank" rel="noopener noreferrer"
                className="text-blue-600 text-sm break-all underline">{info.url}</a>
            </div>
          )}

          {/* Beam purchase id — copy this, then void on Beam */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <p className="text-xs text-gray-500 mb-0.5">Beam purchaseId</p>
            <div className="flex items-center justify-between gap-2">
              <p className="text-blue-900 text-base font-bold tracking-wide break-all">{info.beamPurchaseId || '-'}</p>
              <button onClick={() => copy(info.beamPurchaseId)}
                className="shrink-0 px-3 py-2 rounded-lg bg-blue-700 text-white text-xs font-semibold">
                {copied ? '✓ คัดลอก' : 'คัดลอก'}
              </button>
            </div>
          </div>

          <a href={BEAM_DASHBOARD_URL} target="_blank" rel="noopener noreferrer"
            className="block w-full text-center py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm">
            🔗 เปิด Beam เพื่อ void
          </a>

          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            คัดลอก purchaseId → เปิด Beam → void รายการ → <b>แนบรูปยืนยันการ void</b> ด้านล่าง แล้วกดยืนยัน
          </div>

          {/* Proof image + confirm */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">แนบรูปยืนยันการ void</p>
            <input ref={fileRef} type="file" accept="image/*,.pdf"
              className="block w-full text-sm text-gray-500 file:mr-3 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700" />
          </div>

          {submitError && <p className="text-red-600 text-sm">{submitError}</p>}

          <div className="space-y-2 pt-1">
            <button onClick={confirmVoid} disabled={submitting}
              className="w-full py-3.5 rounded-xl bg-green-600 text-white font-bold disabled:opacity-50">
              {submitting ? 'กำลังบันทึก...' : '✅ ยืนยัน void สำเร็จ (แนบรูปแล้ว)'}
            </button>
            <button onClick={rejectVoid} disabled={submitting}
              className="w-full py-3 rounded-xl border border-gray-300 text-gray-600 font-medium disabled:opacity-50">
              ❌ Void ไม่ได้ → คืนเงิน (+3.5%)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">{children}</div>
    </div>
  );
}
